import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth, obj = auth
  const [initialized, setInitialized] = useState(false);

  const check = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // 401 on first load is expected; log others for diagnostics
      if (e?.response?.status !== 401) {
        // eslint-disable-next-line no-console
        console.warn("[auth] check failed:", e?.message || e);
      }
      setUser(false);
    } finally {
      setInitialized(true);
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  const login = async (email, password) => {
    // Backend sets an httpOnly cookie — we intentionally do NOT read the
    // token from the response body, so it never lives in JS memory or localStorage.
    const { data } = await api.post("/auth/login", { email, password });
    setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[auth] logout failed:", e?.message || e);
    }
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, initialized, login, logout, refresh: check }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
